import { IComponentOptions, IHttpResponse, IPromise, IQService, IScope } from "angular";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { IIndicator, UIErrorHandlingService } from "@gtmhub/error-handling";
import { localize } from "@gtmhub/localization";
import { Ng1UiModalService } from "@gtmhub/shared/components/modal/ng1-modal.service";
import { ITag } from "@gtmhub/tags/models";
import { TagService } from "@gtmhub/tags/tag.service";
import { isTagExistsResponseError } from "@gtmhub/tags/utils";
import { FeatureFlag } from "@webapp/feature-toggles/models/feature-toggles.models";
import { FeatureTogglesFacade } from "@webapp/feature-toggles/services/feature-toggles-facade.service";

export class BulkTagCtrl {
  public searchInTags = "";
  public tags: ITag[];
  public hasCreateTagsPermission: boolean;
  public newTag: string;
  public showForm: boolean;
  public tagExists: boolean;

  public indicators: {
    saving?: IIndicator;
  } = {};

  public static $inject = ["TagService", "$q", "$rootScope", "FeatureTogglesFacade", "UIErrorHandlingService", "Ng1UiModalService", "$scope"];

  constructor(
    private tagService: TagService,
    private $q: IQService,
    private $rootScope: ITraceRootScopeService,
    private featureToggleService: FeatureTogglesFacade,
    private uiErrorHandlingService: UIErrorHandlingService,
    private modalService: Ng1UiModalService,
    private $scope: IScope
  ) {
    this.getTags();

    this.featureToggleService
      .isFeatureAvailable(FeatureFlag.EnableTagManagement)
      .then((featureAvailable) => (this.hasCreateTagsPermission = !featureAvailable || this.$rootScope.hasPermission("CreateTags")));
  }

  public resetForm(): void {
    delete this.indicators.saving;
    this.tagExists = false;
    this.newTag = "";
    this.showForm = false;
  }

  public openForm(): void {
    this.resetForm();
    this.showForm = true;
  }

  public createTag(tagName: string): void {
    this.tagExists = false;

    if (!tagName || !tagName.trim()) {
      return;
    }

    this.$rootScope.traceAction("create_tag", () => {
      this.indicators.saving = { progress: true };
      this.tagService
        .createTag({ title: tagName })
        .then((tag) => {
          this.tags.push(tag);
          this.resetForm();
        })
        .catch((error) => this.handleCreateTagError(error))
        .finally(() => {
          delete this.indicators.saving;
        });
    });
  }

  private showInactiveTagExistsModal(tag: ITag): IPromise<void> {
    const d = this.$q.defer<void>();
    this.modalService.confirm({
      uiTitle: localize("tag_exists_title"),
      uiContent: localize("existing_tag_is_deactivated_cta"),
      uiOkText: localize("activate"),
      uiOnOk: () => {
        this.activateTag(tag).then(d.resolve, d.reject);
      },
      uiCancelText: localize("cancel"),
      uiOnCancel: () => d.resolve(),
    });
    return d.promise;
  }

  private activateTag(tag: ITag): IPromise<void> {
    return this.tagService.changeTagsActivationStatus([tag.id], "activate").then(
      () => {
        this.resetForm();
        this.tags.push({ ...tag, isActive: true });
      },
      (error) => this.uiErrorHandlingService.handleModal(error)
    );
  }

  private handleCreateTagError(error: IHttpResponse<unknown>): IPromise<void> | void {
    // generic error handler
    if (!isTagExistsResponseError(error)) {
      return this.uiErrorHandlingService.handleModal(error);
    }

    // existing deactivated tag
    const tag = error.data.data;
    if (tag.isActive === false) {
      return this.showInactiveTagExistsModal(tag);
    }

    // existing active tag
    this.tagExists = true;
  }

  private getTags(): void {
    this.tagService.getTags().then((response) => {
      this.tags = response.tags;
    });
  }

  public onFormChanged(): void {
    this.$scope.$evalAsync();
  }
}

export const BulkTagComponent: IComponentOptions = {
  template: require("./bulk-tag.component.html"),
  controller: BulkTagCtrl,
};
